import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import { getRefCodesForSignup } from "@brexhq/next/src//telemetry/attributionMetaData";
import { useSignupMutation } from "@brexhq/next/src/api/signup";
import { leadFormMachineContext } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import { TrackSignupSuccess } from "@brexhq/next/src/hooks/useAnalytics";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import {
  getDashboardRedirectUrl,
  PartnerRefValues,
  getLeadProperties,
} from "@brexhq/next/src/routes/signup/Form";
import { usePreviewEnvironment } from "@brexhq/next/src/routes/signup/hooks";
import {
  logAnalyticsError,
  logSignupFailedError,
} from "@brexhq/next/src/telemetry/logging";
import { getSavedMarketingAttributionProperties } from "@brexhq/next/src/utils/analytics";
import { getRedirectUrl } from "@brexhq/next/src/utils/endpoints";
import {
  CompanyTypeV3,
  EmployeeCountV3,
  FundingSourceV3,
  SalesVolumeV3,
  SignupCompanyInput,
  SignupInitialUserInput,
  SignupMetadataInput,
  SignupUserInput,
} from "@brexhq/next/types/brex-api";
import { createMockableHook } from "@brexhq/shared/utils/createMockableHook";
import { isDev } from "@brexhq/shared/utils/env/isDevEnv";

export const encodeEntityId = ({
  rawId,
  typename,
}: {
  typename: string;
  rawId: string;
}): string => btoa(`${typename}:${rawId}`);

const _signupV2 = ({
  trackEarlyAccountSignupSuccess,
  anonymousId,
  partnerRef,
}: {
  partnerRef?: PartnerRefValues | null;
  trackEarlyAccountSignupSuccess: TrackSignupSuccess;
  anonymousId: string;
}) => {
  const { previewEnvironmentNamespace } = usePreviewEnvironment();
  const { signup } = useSignupMutation();
  const location = useLocation();

  const submitUserData = async (formValues: leadFormMachineContext) => {
    const {
      firstName,
      lastName,
      email,
      password,
      selfAttestedRefCode,
      attributionMetadata,
      attributionToken,
      companyName,
      leadToken,
      companyType,
      employeeCount,
      fundingSources,
      salesVolume,
      industry,
      website,
      salesSegment,
    } = getLeadProperties(formValues);

    const redirectUrl: string = getRedirectUrl();

    // These need to be kept in sync manually for now.
    // Definitely not ideal. Could potentially use frontmatter in md and query them,
    // or potentially move them to contentful where they can be updated self-serve by
    // legal team, and updated time will be part of metadata.
    const legalAgreements = [
      // This date needs to be synced to the date here:
      // https://brex.sanity.studio/production/desk/legal;legalPages;12544a1b-8878-48bb-8d20-c26c7e585db6
      { name: "userAgreement", version: "10.25.2022" },
      // This date needs to be synced to the date here:
      // https://brex.sanity.studio/production/desk/legal;legalPages;55e474ad-cd1d-4580-9a7a-3b52fd2ec245
      { name: "privacyPolicy", version: "05.08.2023" },
      // This date needs to be synced to the date here:
      // https://brex.sanity.studio/production/desk/legal;legalPages;8e35bc8b-3e79-4eee-8eb3-d5efc5e75f91
      {
        name: "platformAgreement",
        version: "11.01.2023",
      },
    ];

    const rawSavedMarketingAttributionProperties =
      getSavedMarketingAttributionProperties();

    delete rawSavedMarketingAttributionProperties.ref_code;

    const savedMarketingAttributionProperties = {
      ...rawSavedMarketingAttributionProperties,
      refCode:
        // We treat rc as if it's a ref_code param
        // This is used for referral links in the format
        // brex.com/signup?rc=whatever
        // Related: UrlParameters will clear the ref_code when setting the rc Cookie, so that it can be picked up here
        attributionToken,
    };

    const marketingMetadataForSignupV2 = {
      rc: rawSavedMarketingAttributionProperties.rc,
      // We treat rc as if it's a ref_code param
      // This is used for referral links in the format
      // brex.com/signup?rc=whatever
      // Related: UrlParameters will clear the ref_code when setting the rc Cookie, so that it can be picked up here
      refCode: attributionToken,
      refSrc: rawSavedMarketingAttributionProperties.ref_src,
      groupCode: rawSavedMarketingAttributionProperties.group_code,
      keyword: rawSavedMarketingAttributionProperties.keyword,
      gclid: rawSavedMarketingAttributionProperties.gclid,
      msclkid: rawSavedMarketingAttributionProperties.msclkid,
      adId: rawSavedMarketingAttributionProperties.ad_id,
      adsetId: rawSavedMarketingAttributionProperties.adset_id,
      campaignId: rawSavedMarketingAttributionProperties.campaign_id,
      adgroupId: rawSavedMarketingAttributionProperties.adgroup_id,
      feeditemId: rawSavedMarketingAttributionProperties.feeditem_id,
      targetId: rawSavedMarketingAttributionProperties.target_id,
      locPhysicalMs: rawSavedMarketingAttributionProperties.loc_physical_ms,
      locInterestMs: rawSavedMarketingAttributionProperties.loc_interest_ms,
      matchtype: rawSavedMarketingAttributionProperties.matchtype,
      network: rawSavedMarketingAttributionProperties.network,
      device: rawSavedMarketingAttributionProperties.device,
      creative: rawSavedMarketingAttributionProperties.creative,
      placement: rawSavedMarketingAttributionProperties.placement,
    };
    /**
     * Making the signup inputs properly typed
     */
    const companyDetailsInput: SignupCompanyInput = {
      industry,
      name: companyName,
      segmentationInfo: {
        v3: {
          companyType: companyType as CompanyTypeV3,
          employeeCount: employeeCount as EmployeeCountV3,
          fundingSources: (fundingSources as FundingSourceV3[]) ?? [],
          salesVolume: salesVolume as SalesVolumeV3,
        },
      },
      website,
    };

    const metadataInput: SignupMetadataInput = {
      attribution: {
        /**
         * we don't want to change the original shape of attributionMetaData
         * e.g. fbc, gclid, fbp for consistency purposes
         */
        fbc: attributionMetadata?.fbc,
        fbp: attributionMetadata?.fbp,
        gclid: attributionMetadata?.gclid,
        msclkid: attributionMetadata?.msclkid,
        segmentAnonymousId: anonymousId,
      },
      blackbox: (window as any).iovation_blackbox,
      leadToken,
      legalAgreements,
      marketing: {
        selfAttestedRefCode: selfAttestedRefCode || undefined,
        ...marketingMetadataForSignupV2,
        softSignupFromWebsite: true,
      },
      redirectUrl,
      referralCodes: {
        ...getRefCodesForSignup(location, partnerRef?.token),
        selfAttested: selfAttestedRefCode,
        finalAttribution: attributionToken,
      },
    };
    const userInput: SignupUserInput = {
      email,
      firstName,
      lastName,
      password,
    };

    const signupInput: SignupInitialUserInput = {
      clientMutationId: uuidv4(),
      company: companyDetailsInput,
      metadata: metadataInput,
      user: userInput,
    };

    try {
      const signupMutationResponse = await signup(signupInput);
      if (
        signupMutationResponse.errors ||
        signupMutationResponse?.data.signupInitialUser?.errors
      ) {
        logSignupFailedError(
          new Error(`Signup mutation failed for ${anonymousId}`),
        );
        return Promise.reject({
          title: "Unable to create account",
          message: (
            <div className="_lr-hide dd-privacy-mask">
              We were unable to create your account with email {email}. If you
              already have an account, please check your email. Otherwise,
              please refresh your page to try again. If the problem persists,
              contact <a href="mailto:concierge@brex.com">concierge@brex.com</a>
              .
            </div>
          ),
        });
      }

      /**
       * We want to swallow analytics errors so we don't interrup the actual
       * account creation in the even that analytics somehow fails
       */
      try {
        const analyticsUserProperties = {
          ...(selfAttestedRefCode && {
            self_attested_ref_code: selfAttestedRefCode,
          }),
          ...savedMarketingAttributionProperties,
          softSignupFromWebsite: true,
          anonymousId,
          // Flag not being gated for the leadToken value here, we want to use this for analytics.
          email,
          leadToken,
          companyName,
          companyType,
          employeeCount,
          fundingSources,
          salesVolume,
          industry,
          website,
          salesSegment,
        };

        if (
          signupMutationResponse?.data.signupInitialUser?.result?.customerUserId
        ) {
          trackEarlyAccountSignupSuccess(
            encodeEntityId({
              rawId:
                signupMutationResponse?.data.signupInitialUser?.result
                  .customerUserId,
              typename: "User",
            }),
            analyticsUserProperties,
          );
        } else {
          logAnalyticsError(
            new Error(
              `User id does not exist, unable to track successful signup for id ${anonymousId}`,
            ),
          );
        }
      } catch (error) {
        // swallow analytics errors. these should never block signup
        console.error(error);
        if (error instanceof Error) {
          logAnalyticsError(error);
        }
      }

      const token =
        signupMutationResponse?.data.signupInitialUser?.result?.token;
      const userId =
        signupMutationResponse?.data.signupInitialUser?.result?.customerUserId;

      const session = {
        userId,
        token,
        isJWT: true,
      };

      const shouldUsePreviewEnv = !!(isDev && previewEnvironmentNamespace);

      const dashboardRedirectUrl = getDashboardRedirectUrl(
        session,
        shouldUsePreviewEnv,
        previewEnvironmentNamespace,
      );

      window.location.href = dashboardRedirectUrl.href;
      return;
    } catch (error) {
      if (error instanceof Error) {
        logSignupFailedError(error);
      }
      return Promise.reject({
        title: "Something went wrong",
        message: (
          <>
            Please refresh your page to try again. If the problem persists,
            contact <a href="mailto:concierge@brex.com">concierge@brex.com</a>.
          </>
        ),
      });
    }
  };
  return {
    submitUserData,
  };
};

export const [useSignupV2, MockUseSignupV2] = createMockableHook(_signupV2);
